import { useToast } from '@shared/hooks/use-toast';
import { getErrorMessage } from '@shared/utils/error.utils';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select';
import { SignaturePad, SignaturePadRef } from '@shared/components/ui/SignaturePad';
import { faPlus, faTrash } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { forwardRef, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateReception } from '../../hooks/useCreateReception';
import { PackageInput, PackageType, PACKAGE_TYPE_OPTIONS } from '../../models/Reception';

type PackageFormData = Omit<PackageInput, 'quantity'> & {
  key: string; // Para React keys
  quantity: number | null; // Permite borrar el input temporalmente ('' -> null)
};

const CreateReceptionModal = forwardRef<ModalCenterRef>((_, ref) => {
  const { t } = useTranslation('receptionsModule');
  const { toast } = useToast();
  const createMutation = useCreateReception();
  const signaturePadRef = useRef<SignaturePadRef>(null);

  // Estado del formulario
  const [carrier, setCarrier] = useState('');
  const [vehiclePlate, setVehiclePlate] = useState('');
  const [driverName, setDriverName] = useState('');
  const [packages, setPackages] = useState<PackageFormData[]>([
    { key: crypto.randomUUID(), package_type: 'BOX', quantity: 1 },
  ]);

  const resetForm = () => {
    setCarrier('');
    setVehiclePlate('');
    setDriverName('');
    signaturePadRef.current?.clear();
    setPackages([{ key: crypto.randomUUID(), package_type: 'BOX', quantity: 1 }]);
  };

  const handleCancel = () => {
    (ref as React.RefObject<ModalCenterRef>)?.current?.closeModal();
    resetForm();
  };

  const handleAddPackage = () => {
    setPackages([...packages, { key: crypto.randomUUID(), package_type: 'BOX', quantity: 1 }]);
  };

  const handleRemovePackage = (key: string) => {
    if (packages.length > 1) {
      setPackages(packages.filter((p) => p.key !== key));
    }
  };

  const handlePackageChange = (key: string, field: keyof PackageFormData, value: any) => {
    setPackages(packages.map((p) => (p.key === key ? { ...p, [field]: value } : p)));
  };

  const handleCreate = async () => {
    try {
      // Validaciones básicas
      if (!carrier.trim()) {
        toast({
          title: t('errors.generic'),
          description: t('modal.create.error_required'),
          variant: 'destructive',
        });
        return;
      }

      if (packages.length === 0) {
        toast({
          title: t('errors.generic'),
          description: t('modal.create.error_no_packages'),
          variant: 'destructive',
        });
        return;
      }

      // Validar paquetes antes de generar la firma (evita llamadas/creación innecesaria)
      const hasInvalidQuantity = packages.some(
        (pkg) => pkg.quantity === null || Number.isNaN(pkg.quantity) || pkg.quantity < 1,
      );
      if (hasInvalidQuantity) {
        toast({
          title: t('errors.generic'),
          description: t('modal.create.error_required'),
          variant: 'destructive',
        });
        return;
      }

      let signatureFile: File | undefined;
      if (!signaturePadRef.current?.isEmpty()) {
        const signatureBlob = await signaturePadRef.current?.toBlobJpeg(0.9);
        if (!signatureBlob) {
          toast({
            title: t('modal.create.error_title'),
            description: t('errors.generic'),
            variant: 'destructive',
          });
          return;
        }
        signatureFile = new File([signatureBlob], 'signature.jpg', {
          type: 'image/jpeg',
        });
      }

      // Preparar datos
      const packagesData: PackageInput[] = packages.map((pkg) => ({
        package_type: pkg.package_type,
        package_type_other: pkg.package_type === 'OTHER' ? pkg.package_type_other : null,
        // `handleCreate` valida que `quantity` nunca sea null aquí
        quantity: pkg.quantity as number,
        incident_type: pkg.incident_type || null,
        image_ref: pkg.image_ref || null,
      }));

      await createMutation.mutateAsync({
        carrier: carrier.trim(),
        vehicle_plate: vehiclePlate.trim(),
        driver_name: driverName.trim(),
        ...(signatureFile && { signature_file: signatureFile }),
        packages: packagesData,
      });

      toast({
        title: t('modal.create.success_title'),
        description: t('modal.create.success_description'),
      });

      resetForm();
      handleCancel();
    } catch (error: any) {
      toast({
        title: t('modal.create.error_title'),
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    }
  };

  const isLoading = createMutation.isPending;

  return (
    <ModalCenter
      ref={ref}
      title={t('modal.create.title')}
      type="default"
      width="xl"
      className="h-[calc(100%-30px)] sm:h-auto max-h-[90vh]"
    >
      {/* Contenido con scroll unificado: todo el modal hace scroll junto, sin scroll anidado en paquetes */}
      <div className="space-y-6 flex flex-col">
        {/* Datos de la recepción */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">{t('modal.create.carrier_label')}</label>
            <Input
              placeholder={t('modal.create.carrier_placeholder')}
              value={carrier}
              onChange={(e) => setCarrier(e.target.value)}
              disabled={isLoading}
              data-testid="carrier-input"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">{t('modal.create.vehicle_plate_label')}</label>
            <Input
              placeholder={t('modal.create.vehicle_plate_placeholder')}
              value={vehiclePlate}
              onChange={(e) => setVehiclePlate(e.target.value)}
              disabled={isLoading}
              data-testid="vehicle-plate-input"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">{t('modal.create.driver_name_label')}</label>
            <Input
              placeholder={t('modal.create.driver_name_placeholder')}
              value={driverName}
              onChange={(e) => setDriverName(e.target.value)}
              disabled={isLoading}
              data-testid="driver-name-input"
            />
          </div>
        </div>

        {/* Firma del conductor */}
        <div className="relative" style={{ touchAction: 'none' }}>
          <SignaturePad
            ref={signaturePadRef}
            label={t('modal.create.signature_label')}
            placeholder={t('modal.create.signature_placeholder')}
            height={150}
            disabled={isLoading}
            onChange={() => {}}
          />
        </div>

        {/* Paquetes - sin scroll propio; scroll unificado con el resto del modal */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium text-foreground">{t('modal.create.packages_title')}</h4>
              <p className="text-xs text-muted-foreground">{t('modal.create.packages_description')}</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleAddPackage}
              disabled={isLoading}
              data-testid="add-package-button"
            >
              <FontAwesomeIcon icon={faPlus} className="mr-2 h-3 w-3" />
              {t('button.add_package')}
            </Button>
          </div>

          <div className="space-y-3">
            {packages.map((pkg, index) => (
              <div key={pkg.key} className="p-3 border border-border rounded-lg bg-muted/30 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">
                    {pkg.package_type === 'OTHER' && pkg.package_type_other
                      ? pkg.package_type_other
                      : t(`package_types.${pkg.package_type.toLowerCase()}`)}
                  </span>
                  {packages.length > 1 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemovePackage(pkg.key)}
                      disabled={isLoading}
                      className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                    >
                      <FontAwesomeIcon icon={faTrash} className="h-3 w-3" />
                    </Button>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">{t('modal.create.package_type_label')}</label>
                    <Select
                      value={pkg.package_type}
                      onValueChange={(value) => handlePackageChange(pkg.key, 'package_type', value as PackageType)}
                      disabled={isLoading}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('modal.create.package_type_placeholder')} />
                      </SelectTrigger>
                      <SelectContent>
                        {PACKAGE_TYPE_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {t(option.labelKey)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {pkg.package_type === 'OTHER' && (
                    <div className="space-y-1">
                      <label className="text-xs text-muted-foreground">
                        {t('modal.create.package_type_other_label')}
                      </label>
                      <Input
                        placeholder={t('modal.create.package_type_other_placeholder')}
                        value={pkg.package_type_other || ''}
                        onChange={(e) => handlePackageChange(pkg.key, 'package_type_other', e.target.value)}
                        disabled={isLoading}
                      />
                    </div>
                  )}

                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">{t('modal.create.quantity_label')}</label>
                    <Input
                      type="number"
                      min={1}
                      step={1}
                      placeholder={t('modal.create.quantity_placeholder')}
                      value={pkg.quantity ?? ''}
                      data-testid={`package-quantity-input-${index}`}
                      onChange={(e) =>
                        // Mantener el valor editable: al borrar, no forzamos `1`
                        // ('' -> null) y al escribir, guardamos el número.
                        handlePackageChange(
                          pkg.key,
                          'quantity',
                          (() => {
                            const raw = e.target.value;
                            if (raw === '') return null;
                            const parsed = parseInt(raw, 10);
                            return Number.isNaN(parsed) ? null : parsed;
                          })(),
                        )
                      }
                      disabled={isLoading}
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">{t('modal.create.incident_type_label')}</label>
                    <Input
                      placeholder={t('modal.create.incident_type_placeholder')}
                      value={pkg.incident_type || ''}
                      onChange={(e) => handlePackageChange(pkg.key, 'incident_type', e.target.value || null)}
                      disabled={isLoading}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Botones */}
        <div className="flex justify-end gap-2 pt-4 border-t border-border">
          <Button variant="outline" onClick={handleCancel} size="lg" disabled={isLoading}>
            {t('button.cancel')}
          </Button>
          <Button variant="primary" onClick={handleCreate} size="lg" disabled={isLoading}>
            {isLoading ? t('button.creating') : t('button.create')}
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

CreateReceptionModal.displayName = 'CreateReceptionModal';

export default CreateReceptionModal;
